import clsx from "clsx";
import React, { CSSProperties, useState } from "react";
import { PropsValue, StylesConfig } from "react-select";
import Icon from "../../../components/icon/Icon";
import { useTheme } from "../../../hooks";
import { SelectThemeType } from "../../../types";
import { BaseInputProps } from "../../../types/InputTypes";
import { RequiredInput } from "../../required";
import { ReadOnlyFormatter } from "../input";
import Label from "../label/Label";
import { SelectContainer, StyledSelect } from "./SelectStyles";

const IndicatorSeparator = () => <span style={{ display: "none" }} />;

export interface ValueOpt<SelectValueType> extends PropsValue<any> {
  value: SelectValueType;
  label: React.ReactNode;
  disabled?: boolean;
}

export type SingleSelectStyles = StylesConfig<ValueOpt<any>, false>;
export type MultiSelectStyles = StylesConfig<ValueOpt<any>, true>;
export type BCRSelectStyles = SingleSelectStyles & {
  disabled?: {
    background?: string;
  };
};

export type SelectProps<SelectValueType> = BaseInputProps & {
  placeholder?: string;
  components?: object;
  required?: boolean;
  styles?: SingleSelectStyles | MultiSelectStyles | BCRSelectStyles;
  isMulti?: boolean;
  autofocus?: boolean;
  onChange: (value?: ValueOpt<SelectValueType> | ValueOpt<SelectValueType>[]) => void;
  options: ValueOpt<SelectValueType>[];
  value?: null | ValueOpt<SelectValueType> | ValueOpt<SelectValueType>[];
  portal?: null | HTMLElement;
};

export const NaOpt: ValueOpt<string> = { value: "N/A", label: "" };

const Select = <SelectValueType,>(props: SelectProps<SelectValueType>) => {
  const {
    name,
    className = "",
    id,
    label,
    placeholder,
    required = false,
    disabled = false,
    readOnly = false,
    clearable = false,
    isMulti = false,
    autofocus = false,
    options,
    value,
    onChange,
    portal = document.body,
    gutterBottom = false,
    styles = {},
    showNA = false,
  } = props;

  const {
    input = {},
    container = {},
    multiValue = {},
    multiValueLabel = {},
    multiValueRemove = {},
    menu = {},
    menuPortal = {},
    control,
    option = {},
    valueContainer = {},
    indicatorsContainer = {},
    dropdownIndicator = {},
    clearIndicator = {},
    singleValue = {},
  } = styles;

  const { Theme } = useTheme();
  //todo: fix missing fields
  //@ts-ignore
  const StylesOverride: SelectThemeType = { ...Theme.select, ...styles };
  const DropdownIndicator = () => (
    <Icon
      iconName="fa-caret-down"
      className="me-3"
      styles={{ color: StylesOverride.actionColor }}
      disabled={disabled}
    />
  );

  const SelectStyles = {
    container: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      ...container,
    }),
    input: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      color: StylesOverride.color,
      ...input,
    }),
    placeholder: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      fontStyle: StylesOverride.placeholder.fontStyle,
      color: StylesOverride.placeholder.color,
      fontWeight: StylesOverride.placeholder.fontWeight,
      fontSize: "0.875rem",
      opacity: 1,
    }),
    multiValue: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      color: StylesOverride.color,
      background: StylesOverride.multiBackground,
      borderRadius: StylesOverride.multiBorderRadius,
      ...multiValue,
    }),
    multiValueLabel: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      ...multiValueLabel,
    }),
    multiValueRemove: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      color: StylesOverride.actionColor,
      borderRadius: "14px",
      height: "20px",
      width: "20px",
      marginTop: "2px",
      marginRight: "2px",
      "&:hover": {
        color: StylesOverride.hover.color,
        backgroundColor: StylesOverride.hover.background,
      },
      background: StylesOverride.background,
      ...multiValueRemove,
    }),
    menu: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      zIndex: 2000,
      background: StylesOverride.background,
      color: StylesOverride.color,
      ...menu,
    }),
    menuPortal: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      zIndex: 2050,
      ...menuPortal,
    }),
    option: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      background: state.isFocused ? StylesOverride.hover.background : StylesOverride.background,
      color: state.isFocused ? StylesOverride.hover.color : StylesOverride.color,
      ...option,
    }),
    valueContainer: (selectStyles: CSSProperties, state: any) => {
      const styles = {
        ...selectStyles,
        background: StylesOverride.background,
        fontSize: Theme.input.size,
        ...valueContainer,
      };
      if (state.isDisabled) {
        styles.backgroundColor = `${StylesOverride.disabled.background} !important`;
      }
      return {
        ...styles,
      };
    },
    indicatorsContainer: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      padding: 0,
      background: StylesOverride.background,
      ...indicatorsContainer,
    }),
    dropdownIndicator: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      ...dropdownIndicator,
    }),
    clearIndicator: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      padding: StylesOverride.clearIndicator.padding,
      ...clearIndicator,
    }),
    control: (selectStyles: CSSProperties, state: any) => {
      const shadowTheme = {
        valid: "rgba(0,123,255,.25)",
        invalid: "rgba(220,53,69,.25)",
      };
      const styles = {
        ...selectStyles,
        minWidth: 60,
        borderColor: StylesOverride.borderColor,
        backgroundColor: StylesOverride.background,
        ...control,
      };
      if (state.isFocused) {
        //@ts-ignore
        styles.boxShadow = `0 0 0 0.2rem ` + shadowTheme["valid"];
      }
      if (state.isDisabled) {
        styles.backgroundColor = `${StylesOverride.disabled.background} !important`;
      }
      return {
        ...styles,
      };
    },
    singleValue: (selectStyles: CSSProperties, state: any) => ({
      ...selectStyles,
      color: StylesOverride.color,
      ...singleValue,
    }),
  };

  const [focus, setFocus] = useState(false);
  const toggleFocus = () => {
    setFocus(!focus);
  };

  //@ts-ignore
  const onChangeSelect = (option) => {
    //@ts-ignore
    onChange(option);
  };

  const getMultiReadOnlyString = (values: ValueOpt<SelectValueType>[] | ValueOpt<SelectValueType> | null): string => {
    if (Array.isArray(values) && values.length) {
      return values.map((v) => v.label).join(", ");
    }
    return "";
  };

  const toggleNA = (value: boolean) => {
    if (value) {
      //@ts-ignore
      onChange(NaOpt);
    } else {
      onChange(undefined);
    }
  };

  //@ts-ignore
  const isNA: boolean = showNA && NaOpt === value;

  return (
    <SelectContainer className={clsx({ "mb-2": gutterBottom })}>
      {!!label && (
        <Label
          htmlFor={id}
          required={required && !readOnly}
          showNA={showNA && !readOnly}
          isNA={isNA}
          onChange={toggleNA}
        >
          {label}
        </Label>
      )}
      {!readOnly ? (
        <RequiredInput required={required && !disabled && !readOnly}>
          <StyledSelect
            className={clsx(className, { "is-na": isNA })}
            //@ts-ignore
            styles={SelectStyles}
            components={{ IndicatorSeparator, DropdownIndicator }}
            name={name}
            id={id}
            onFocus={() => {
              toggleFocus();
            }}
            onBlur={() => {
              toggleFocus();
            }}
            placeholder={placeholder}
            isDisabled={disabled || isNA}
            menuPortalTarget={portal}
            isClearable={clearable}
            isMulti={isMulti}
            onChange={onChangeSelect}
            options={options}
            value={value === undefined || isNA ? null : value}
            closeMenuOnSelect={!isMulti}
            bcrStyles={StylesOverride}
            autoFocus={autofocus}
          />
        </RequiredInput>
      ) : (
        <ReadOnlyFormatter
          value={
            isMulti
              ? getMultiReadOnlyString(value as ValueOpt<SelectValueType>[])
              : !!value
              ? (value as ValueOpt<SelectValueType>).label
              : ""
          }
          id={id}
        />
      )}
    </SelectContainer>
  );
};

export default Select;
